import type { FdFeatureIcon } from "./fdModuleIcons";

export interface ModuleFeature {
  title: string;
  icon: FdFeatureIcon;
  description?: string;
  items?: string[];
}

export interface FdModule {
  id: string;
  title: string;
  description: string;
  features: ModuleFeature[];
}

export const fdModules: FdModule[] = [
  {
    id: "user-app",
    title: "User App Features",
    description:
      "A complete customer-facing food ordering experience — from discovery and search to payment, tracking, and reviews.",
    features: [
      {
        title: "Social Login",
        icon: "social-login",
        description:
          "Register using Facebook, Twitter, and other major social networks, or use our custom registration form if you prefer.",
      },
      {
        title: "Order Navigation",
        icon: "order-navigation",
        description:
          "Track your order with delivery person name, photo, live route, and the ability to guide them if they miss the path.",
      },
      {
        title: "Book a Table",
        icon: "book-table",
        description:
          "Pre-book a table at your favourite restaurant and spend quality time with your close ones.",
      },
      {
        title: "Profile Handling",
        icon: "profile",
        description:
          "Insert and edit your profile using login credentials to display the information you choose.",
      },
      {
        title: "Email and SMS Integration",
        icon: "email-sms",
        description:
          "Receive regular updates on offers, discounts, and detailed invoices via both email and SMS.",
      },
      {
        title: "Call Integration",
        icon: "call",
        description:
          "Integrated calling lets customers enquire about delivery status and delivery persons reach customers directly.",
      },
      {
        title: "Find New Restaurants",
        icon: "find-restaurants",
        description:
          "Explore restaurants in your territory and order your favourite food online after registration.",
      },
      {
        title: "Cuisine Based Search",
        icon: "cuisine-search",
        description:
          "Diversified search options including cuisine-based discovery to help you find your favourite food varieties.",
      },
      {
        title: "Food Based Search",
        icon: "food-search",
        description:
          "Check availability of particular food categories across selective localities and plan your order accordingly.",
      },
      {
        title: "Area Based Search",
        icon: "area-search",
        description:
          "Updated navigation connects with all restaurants in and around your city to suggest the best food spots in your locality.",
      },
      {
        title: "Verify OTP",
        icon: "otp",
        description:
          "After social login or app registration, an auto-generated OTP must be entered to initiate app usage.",
      },
      {
        title: "Wallet Integration",
        icon: "wallet",
        description:
          "Fully digitised payments — utilise a digital wallet for any range of payment with no pay-on-delivery option.",
      },
      {
        title: "Track Your Order",
        icon: "track-order",
        description:
          "Live order tracking gives you a minute-by-minute update on your order's location.",
      },
      {
        title: "Cancel Order",
        icon: "cancel-order",
        description:
          "Cancel your order before the delivery person picks up the food from the restaurant.",
      },
      {
        title: "Detailed Invoice",
        icon: "invoice",
        description:
          "A detailed invoice is generated for every order to provide complete clarity over your purchase.",
      },
      {
        title: "Find Your Favourite Restaurant",
        icon: "favourite",
        description:
          "Mark and quickly access your preferred restaurants whenever you browse the app.",
      },
      {
        title: "Place Your Order",
        icon: "place-order",
        description:
          "Choose from multiple ordering options — search for specific food items and see all restaurants offering them.",
      },
      {
        title: "Reviews and Ratings",
        icon: "reviews",
        description:
          "View and create reviews about restaurant quality, enabling peer-to-peer recommendations for better choices.",
      },
      {
        title: "Offers and Discounts",
        icon: "offers",
        description:
          "Admin-generated offers and coupons with validation periods — nourishing both customer and merchant wellness.",
      },
    ],
  },
  {
    id: "delivery-app",
    title: "Delivery App Features",
    description:
      "Purpose-built tools for delivery partners — from onboarding and navigation to earnings and order management.",
    features: [
      {
        title: "Social Login",
        icon: "social-login",
        description:
          "Drivers sign up through social media accounts, saving time for both delivery partners and administrators.",
      },
      {
        title: "OTP Verification",
        icon: "otp",
        description:
          "Three-tier enrolment with OTP verification sent to the registered mobile number or email from social accounts.",
      },
      {
        title: "Navigation Guide",
        icon: "navigation-guide",
        description:
          "Updated route navigation providing the shortest path to reach the restaurant and customer location.",
      },
      {
        title: "Scheduled Booking",
        icon: "scheduled-booking",
        description:
          "Customers reserve delivery times; the app assigns delivery persons based on availability near the restaurant or residence.",
      },
      {
        title: "Live Navigation",
        icon: "live-navigation",
        description:
          "GPS-powered live navigation assists delivery persons through the shortest, lowest-traffic path to destinations.",
      },
      {
        title: "Earning Modes",
        icon: "earnings",
        description:
          "Rs. 4/km for the first 4 km and Rs. 6/km beyond, with incentives for extra deliveries and overtime. Full-time partners may cancel up to three orders daily; additional cancellations incur Rs. 30 per order with no monthly incentive.",
      },
      {
        title: "Dashboard",
        icon: "dashboard",
        description:
          "Dedicated dashboard with assigned delivery spots, addresses, restaurant names, completed tasks, and route maps.",
      },
      {
        title: "On-board Calling",
        icon: "call",
        description:
          "Call restaurants directly from the app to confirm order availability before pickup.",
      },
      {
        title: "Spot Bookings",
        icon: "spot-booking",
        description:
          "Normal customer orders assigned to delivery persons based on proximity to the particular restaurant.",
      },
    ],
  },
  {
    id: "admin-merchant",
    title: "Admin App for Merchant Features",
    description:
      "Centralised admin control for merchants, orders, payments, commissions, and platform-wide settings.",
    features: [
      {
        title: "New Merchant Registration",
        icon: "merchant-registration",
        description:
          "Register new merchants with restaurant name, registration number, address, contact details, cuisine type, food items, and enrolment date.",
      },
      {
        title: "Incoming Order Management",
        icon: "order-management",
        description:
          "Handle incoming orders categorised by online orders or table bookings with payment mode, food categories, total price, and tax details.",
      },
      {
        title: "Merchant Payment Management",
        icon: "payment-management",
        description:
          "Transact payments to merchants and manage entire transaction details with ease.",
      },
      {
        title: "Commission Management",
        icon: "commission",
        description:
          "Configure commission setups per partnered restaurant based on business deals for seamless payment handling.",
      },
      {
        title: "Invoice Handling",
        icon: "invoice-handling",
        description:
          "Detailed merchant invoices with total cost, tax incurred, commission fixed, and payment mode. Admin retains a separate copy.",
      },
      {
        title: "Ratings",
        icon: "ratings",
        description:
          "Restaurants classified by star ratings and categorised for display to future users.",
      },
      {
        title: "Social Settings",
        icon: "social-settings",
        description:
          "Store and manage social media accounts with editable options for self-promotional activities across platforms.",
      },
      {
        title: "Notification Settings",
        icon: "notifications",
        description:
          "Segregated email and SMS notifications for users, merchants, and drivers for timely intimation.",
      },
      {
        title: "Contact Details",
        icon: "contact-details",
        description:
          "Complete merchant contact details for order and membership information whenever required.",
      },
      {
        title: "Cancelled Orders",
        icon: "cancelled-orders",
        description:
          "Immediate notification to the concerned restaurant when a customer cancels an order to stop processing.",
      },
    ],
  },
];

export const fdAdvantages = [
  "Complete multi-vendor food ordering and delivery platform for customers, drivers, merchants, and admins",
  "Swiggy, Uber Eats, and Zomato clone with deep-rooted search, tracking, and payment functionalities",
  "Social login and OTP verification across all user types for fast, secure onboarding",
  "Live order tracking with delivery person details, route maps, and integrated calling",
  "Cuisine-based, food-based, and area-based search to discover restaurants and dishes nearby",
  "Digital wallet integration with detailed invoicing via email and SMS",
  "Table booking alongside online ordering for dine-in customers",
  "Commission management, merchant payments, and ratings for multi-chain restaurant operations",
  "Offers, coupons, and discount management with validation periods",
  "High-end technology frameworks deployed for engaging, device-friendly multi-platform experience",
];

export const fdWorkflowSteps = [
  {
    title: "Customer Discovery",
    text: "Users register via social login or custom form, verify OTP, and explore restaurants through cuisine, food, and area-based search.",
  },
  {
    title: "Order Placement",
    text: "Customers browse menus, add items to cart, apply offers, pay via digital wallet, and receive detailed invoices by email and SMS.",
  },
  {
    title: "Restaurant Processing",
    text: "Merchants receive incoming orders through the admin panel, process food items, and manage table bookings alongside online orders.",
  },
  {
    title: "Delivery Assignment",
    text: "Spot or scheduled bookings assign the nearest available delivery person with live navigation and route guidance to the restaurant.",
  },
  {
    title: "Live Tracking",
    text: "Customers track orders in real time with delivery person details, while drivers use GPS navigation and onboard calling.",
  },
  {
    title: "Completion & Feedback",
    text: "Orders are delivered, commissions are settled, and customers leave reviews and ratings to help future diners choose wisely.",
  },
];

export const fdPlatformApps = [
  {
    title: "Consumer App",
    description:
      "Similar to the delivery person, users register through social media or a custom form. Explore restaurants, search by cuisine, place orders, track deliveries, and manage profiles.",
    icon: "smartphone",
  },
  {
    title: "Delivery App",
    description:
      "Drivers enrol via social login with OTP verification. Access live navigation, earnings dashboard, spot and scheduled bookings, and onboard calling.",
    icon: "navigation",
  },
  {
    title: "Merchant App",
    description:
      "Multi-vendor food booking with end-to-end order management — incoming orders, table bookings, invoices, and payment tracking.",
    icon: "store",
  },
  {
    title: "Admin App",
    description:
      "Central control for merchant registration, commission management, ratings, notifications, social settings, and cancelled order handling.",
    icon: "shield",
  },
];

export const fdAppShowcase = [
  {
    title: "Restaurant Menu",
    description:
      "Browse detailed menus with ratings, cuisine types, delivery time, and category-based navigation.",
    placeholderLabel: "Restaurant Menu Screen",
    placeholderHint: "4.5 rating · Continental · Chinese · Thai",
  },
  {
    title: "Smart Search & Filters",
    description:
      "Find nearest shops by area, filter by offers (20%–80%), and select minimum star ratings before applying.",
    placeholderLabel: "Search & Filter Screen",
    placeholderHint: "Area tags · Offers slider · Star ratings",
  },
  {
    title: "Restaurant Listings",
    description:
      "Explore nearby restaurants with ratings, delivery times, average pricing, minimum order, and promotional offers.",
    placeholderLabel: "Restaurant Listing Screen",
    placeholderHint: "9 restaurants · Filter · Sort by relevance",
  },
  {
    title: "Location Discovery",
    description:
      "Auto-locate or manually enter your area, then find the best restaurants around you instantly.",
    placeholderLabel: "Start Your Order Screen",
    placeholderHint: "Locating you · Enter manually · Find restaurants",
  },
];
